import React, { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { APIClient } from "@/api";
import {
  setStoredUserInfo,
  setStoredUserPermissions,
} from "@/lib/auth-storage";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string, navigate?: any) => Promise<any>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state changed:", event, session);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string, navigate?: any) => {
    const response = await fetch(
      `${import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL}/user/doLogin`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username: email, password }),
        credentials: "include",
      }
    );

    const loginResponse = await response?.json();

    if (!response?.ok) {
      throw new Error(loginResponse?.errorMessage || "Login failed");
    }

    if (loginResponse?.mfaRequired && navigate) {
      navigate("/auth/verify-mfa", { state: { email } });
      return { mfaRequired: true };
    }

    setStoredUserPermissions(loginResponse);

    const userInfoResponse = await APIClient("/user/getCurrentUserInfo");

    console.log(userInfoResponse.data, "userInfoResponse.data----");

    setStoredUserInfo(userInfoResponse.data);

    return { mfaRequired: false };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
